import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Form, Space, Tooltip, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import {
  clearSettingsResult,
  getAuthQrBase64,
  loadSettingsStatus,
  resetAuthenticator,
  setPreferredMfa,
  verifyAuthenticator,
} from "../../store/user/general/userActions";
import CustomCheckbox from "../shared/form/reactHookForm/CustomCheckbox";
import CustomInput from "../shared/form/reactHookForm/CustomInput";
import CustomRadioGroup from "../shared/form/reactHookForm/CustomRadioGroup";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const mfaSchema = yup.object({
  Provider: yup.mixed().oneOf(["Email", "Phone", "Authenticator"]).required(),
  SmsConsent: yup
    .boolean()
    .when("Provider", (Provider, schema) =>
      Provider === "Phone" ? schema.oneOf([true], "") : schema.strip()
    ),
  VerifyCode: yup
    .string()
    .trim()
    .when("Provider", (Provider, schema) =>
      Provider === "Authenticator"
        ? schema
            .required("Enter the 6-digit code.")
            .matches(/^\d{6}$/, "Code must be 6 digits.")
        : schema.strip()
    ),
});

export const MfaEditDrawer = () => {
  //
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isSubmitting, isValid },
  } = useForm({
    mode: "all",
    defaultValues: {
      Provider: "Email",
      SmsConsent: false,
      VerifyCode: "",
    },
    resolver: yupResolver(mfaSchema),
  });

  const Provider = watch("Provider");

  //
  const dispatch = useDispatch();

  //
  const status = useSelector((state) => state.user.general.settings.status);
  const operationResult = useSelector(
    (state) => state.user.general.settings.operationResult
  );
  const formattedKey = useSelector(
    (state) => state.user.general.settings.formattedKey
  );
  const loading = useSelector((state) => state.user.general.settings.loading);
  const qrLoading = useSelector(
    (state) => state.user.general.settings.qrLoading
  );
  const qrBase64 = useSelector((state) => state.user.general.settings.qrBase64);
  const qrDataUrl = qrBase64 ? `data:image/png;base64,${qrBase64}` : null;

  //
  const phoneConfirmed = status?.phoneNumberConfirmed ?? false;
  const authenticatorConfigured =
    status?.isTwoFactorEnabled && status?.preferredProvider === "Authenticator";

  //
  const [alertMessage, setAlertMessage] = useState(null);

  //
  const options = useMemo(() => {
    const base = [
      { label: "Email", value: "Email" },
      {
        label: (
          <div>
            Authenticator
            <Tooltip title="Scan the provided QR code in Microsoft Authenticator or Google Authenticator to get started">
              <QuestionCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
          </div>
        ),
        value: "Authenticator",
      },
    ];
    if (phoneConfirmed) base.push({ label: "Phone", value: "Phone" });
    return base;
  }, [phoneConfirmed]);

  //
  useEffect(() => {
    if (status) {
      reset({
        Provider: status?.preferredProvider || "Email",
        SmsConsent: false,
        VerifyCode: "",
      });
      trigger();
    }
  }, [dispatch, status]);

  useEffect(() => {
    dispatch(clearSettingsResult());
    return () => {
      dispatch(clearSettingsResult());
    };
  }, [dispatch]);

  useEffect(() => {
    if (operationResult) {
      setAlertMessage({
        type: operationResult.Outcome === "Success" ? "success" : "error",
        text: operationResult.Message,
      });
      if (operationResult.Outcome === "Success") {
        dispatch(loadSettingsStatus());
      }
    }
  }, [operationResult]);

  const onResetAuthenticator = () => {
    dispatch(resetAuthenticator());
  };

  const onSubmit = async (values) => {
    setAlertMessage(null);
    if (values.Provider === "Authenticator" && !authenticatorConfigured) {
      const code = values.VerifyCode?.trim();
      dispatch(
        verifyAuthenticator({
          Code: code,
        })
      );
      setValue("VerifyCode", "");
    } else {
      dispatch(setPreferredMfa(values.Provider));
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {alertMessage && (
        <Alert type={alertMessage.type} message={alertMessage.text} showIcon />
      )}

      <Title level={5} style={{ marginBottom: 0 }}>
        Choose your default 2FA method
      </Title>

      <Form layout="vertical" size="small" onFinish={handleSubmit(onSubmit)}>
        <CustomRadioGroup
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
          control={control}
          formLabel=""
          name="Provider"
          afterChange={(e) => {
            if (e?.target?.value == "Authenticator") {
              if (!authenticatorConfigured) {
                dispatch(getAuthQrBase64());
              }
              setValue("VerifyCode", "");
            }
            trigger();
          }}
          options={options}
          customField1={options}
        />

        {Provider === "Phone" && (
          <CustomCheckbox
            control={control}
            formLabel=""
            formStyle={{ marginBottom: 0 }}
            name="SmsConsent"
            style={{ marginBottom: "12px", fontStyle: "italic" }}
          >
            I consent to receiving multi-factor authentication codes at my phone
            number.
          </CustomCheckbox>
        )}

        {Provider === "Authenticator" && (
          <>
            {authenticatorConfigured ? (
              <div>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text>Authenticator is configured.</Text>
                  <Button danger onClick={onResetAuthenticator}>
                    Reset authenticator
                  </Button>
                </Space>
              </div>
            ) : (
              <div>
                <Space align="start" size="large" wrap>
                  <div>
                    <div
                      style={{
                        width: 220,
                        height: 220,
                        border: "1px solid #eee",
                        borderRadius: 8,
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden",
                        background: "#fff",
                      }}
                    >
                      {qrDataUrl && !qrLoading ? (
                        <img
                          src={qrDataUrl}
                          alt="Authenticator QR"
                          style={{
                            width: 220,
                            height: 220,
                            display: "block",
                          }}
                        />
                      ) : (
                        <div style={{ color: "#999" }}>Loading…</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{ fontSize: 12, color: "#666", marginBottom: 4 }}
                    >
                      Or enter the key manually:
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 18,
                        background: "#f7f7f7",
                        padding: "8px 12px",
                        borderRadius: 6,
                        display: "inline-block",
                      }}
                    >
                      {formattedKey || "xxxx xxxx xxxx xxxx"}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                      Issuer:{" "}
                      <strong>{process.env.REACT_APP_ISSUER_NAME}</strong> — 6
                      digits — Time-based (30s)
                    </div>
                  </div>
                </Space>

                <Space.Compact style={{ marginTop: 16, width: "100%" }}>
                  <CustomInput
                    inputMode="numeric"
                    pattern="[0-9]*"
                    formLabel=""
                    placeholder="6-digit code"
                    control={control}
                    name="VerifyCode"
                    id="VerifyCode"
                  />
                </Space.Compact>
              </div>
            )}
          </>
        )}

        <Space style={{ marginTop: 12 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading || isSubmitting}
            disabled={
              !isValid ||
              (authenticatorConfigured && Provider === "Authenticator")
            }
          >
            Save
          </Button>
        </Space>
      </Form>
    </Space>
  );
};
